import { gql, DocumentNode } from '@apollo/client';
import { Box, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '../../../Button';
import MuiModal, { MuiModalProps } from '../../../../mui/MuiModal/MuiModal';
import styles from './InvoiceViewPaymentInstallmentPaymentModal.module.scss';
import rpcShared from '@rockpapercoin/rpc-shared';
import {
	InvoiceViewPaymentInstallmentPaymentModalFragment,
	useInvoiceViewPaymentInstallmentPaymentModal_UpdatePaidInstallmentMutation,
	useInvoiceViewPaymentInstallmentPaymentModal_RecordOfflinePaymentMutation,
	InstallmentStatus,
	PaymentType,
	PaymentProcessor,
	useInvoiceViewPaymentInstallmentPaymentModal_UnrecordOfflinePaymentOrRefundMutation,
	RecordOfflinePaymentOrRefundType,
	InvoiceViewPaymentInstallmentPaymentModalInvoiceFragment,
} from '#types';
import { InvoiceViewPaymentMethod } from '../InvoiceViewPaymentMethod';
import { DatePicker } from '../../../DatePicker';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore.js';
import MuiTextArea from '../../../../mui/MuiTextArea/MuiTextArea';
import { useToast } from '../../../../hooks/useToast';
import { InvoiceViewPaymentInstallmentStatus } from '../InvoiceViewPaymentInstallmentStatus';
import { PaymentInstallmentOfflinePaymentTypeSelect } from '../PaymentInstallmentOfflinePaymentTypeSelect';
import { InvoiceViewTotals } from '../../InvoiceViewTotalsAndAutoPay/InvoiceViewTotals';

dayjs.extend( isSameOrBefore );

const invoiceViewPaymentInstallmentPaymentModalInvoiceFragment = gql`
	fragment invoiceViewPaymentInstallmentPaymentModalInvoice on Invoice {
		id
		discountAmount
		lineItems {
			id
			name
			taxPercentage
			rate
			quantity
		}
		paymentInstallments {
			id
			name
			amountDue
			dueDate
			paymentDate
			refundDate
			convenienceFee
			amountPaid
			gratuityPaid
			amountRefunded
		}
		contact {
			...getViewingUserDocumentRelationshipContact
		}
		organization {
			id
			country
		}
		...invoiceViewTotalsInvoice
	}
	${ InvoiceViewTotals.fragments.invoiceViewTotalsInvoiceFragment }
`;

const invoiceViewPaymentInstallmentPaymentModalFragment = gql`
	fragment invoiceViewPaymentInstallmentPaymentModal on PaymentInstallment {
		id
		name
		amountDue
		status
		dueDate
		processor
		paymentDate
		paymentType
		paymentNote
		...invoiceViewPaymentInstallmentStatus
		...invoiceViewPaymentMethodPaymentInstallment
	}
	${ InvoiceViewPaymentMethod.fragments
		.invoiceViewPaymentMethodPaymentInstallmentFragment }
	${ InvoiceViewPaymentInstallmentStatus.fragments
		.invoiceViewPaymentInstallmentStatusFragment }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const invoiceViewPaymentInstallmentPaymentModal_updatePaidInstallment = gql`
	mutation invoiceViewPaymentInstallmentPaymentModal_updatePaidInstallment(
		$data: UpdatePaidInstallmentInput!
		$where: PaymentInstallmentWhereUniqueInput!
	) {
		updatePaidInstallment(data: $data, where: $where) {
			id
			status
			paymentInstallments {
				...invoiceViewPaymentInstallmentPaymentModal
			}
		}
	}
	${ invoiceViewPaymentInstallmentPaymentModalFragment }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const invoiceViewPaymentInstallmentRefundModal_recordOfflinePayment = gql`
	mutation invoiceViewPaymentInstallmentPaymentModal_recordOfflinePayment(
		$data: RecordOfflinePaymentInput!
		$where: PaymentInstallmentWhereUniqueInput!
	) {
		recordOfflinePayment(data: $data, where: $where) {
			id
			status
			isPastDue
			paymentInstallments {
				...invoiceViewPaymentInstallmentPaymentModal
			}
		}
	}
	${ invoiceViewPaymentInstallmentPaymentModalFragment }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const invoiceViewPaymentInstallmentPaymentModal_unrecordOfflinePaymentOrRefund = gql`
	mutation invoiceViewPaymentInstallmentPaymentModal_unrecordOfflinePaymentOrRefund(
		$data: RecordOfflinePaymentOrRefundInput!
		$where: PaymentInstallmentWhereUniqueInput!
	) {
		unrecordOfflinePaymentOrRefund(data: $data, where: $where) {
			id
			status
			isPastDue
		}
	}
`;

const ModalValue: React.FC<{
	className?: string;
	label: string;
	value: string | React.JSX.Element;
	isBold?: true;
	isVisible?: boolean;
	usePlaceholderBoxIfInvisible?: boolean;
}> = ( {
	className,
	label,
	value,
	isBold,
	isVisible = true,
	usePlaceholderBoxIfInvisible = false,
} ) =>
	isVisible ? (
		<Box className={ className }>
			<Typography color='gray' className={ styles.label }>
				{ label }
			</Typography>
			{ typeof value === 'string' ? (
				<Typography
					className={ isBold ? styles.bold : undefined }
					data-cy={ label.replace( ' ', '-' ).toLowerCase() }
				>
					{ value }
				</Typography>
			) : (
				value
			) }
		</Box>
	) : usePlaceholderBoxIfInvisible ? (
		<Box />
	) : null;

type HandleOnCloseProps = Parameters<
	NonNullable<MuiModalProps['muiModalProps']['onClose']>
>;

export type InvoiceViewPaymentInstallmentPaymentModalProps = {
	paymentInstallment?: InvoiceViewPaymentInstallmentPaymentModalFragment;
	invoice?: InvoiceViewPaymentInstallmentPaymentModalInvoiceFragment;
	requestingUserBaseId?: string;
} & Pick<MuiModalProps['muiModalProps'], 'onClose'>;

type InvoiceViewPaymentInstallmentPaymentModalPropsWithFragment =
	React.FC<InvoiceViewPaymentInstallmentPaymentModalProps> & {
		fragments: {
			invoiceViewPaymentInstallmentPaymentModalFragment: DocumentNode;
			invoiceViewPaymentInstallmentPaymentModalInvoiceFragment: DocumentNode;
		};
	};

export const InvoiceViewPaymentInstallmentPaymentModal: InvoiceViewPaymentInstallmentPaymentModalPropsWithFragment =
	( { paymentInstallment, invoice, requestingUserBaseId, onClose } ) => {
		const { showError, showSuccess } = useToast();
		const getPaymentTypeForModal = useCallback(
			( installment?: InvoiceViewPaymentInstallmentPaymentModalFragment ) =>
				installment?.status === InstallmentStatus.Unpaid
					? rpcShared.invoices.isOfflinePaymentType( installment.paymentType )
						? installment.paymentType
						: undefined
					: installment?.paymentType || undefined,
			[]
		);
		const [ paymentType, setPaymentType ] = useState( () =>
			getPaymentTypeForModal( paymentInstallment )
		);
		const [ validatePaymentType, setValidatePaymentType ] = useState<
			string | undefined
		>();
		const [ paymentDate, setPaymentDate ] = useState(
			paymentInstallment?.paymentDate
		);
		const [ validatePaymentDate, setValidatePaymentDate ] = useState<
			string | undefined
		>();
		const [ paymentNote, setPaymentNote ] = useState(
			paymentInstallment?.paymentNote || ''
		);
		useEffect( () => {
			setPaymentType( getPaymentTypeForModal( paymentInstallment ) );
			setPaymentDate( ( prevState ) =>
				paymentInstallment?.paymentDate
					? paymentInstallment.paymentDate
					: prevState
			);
			setPaymentNote( ( prevState ) =>
				paymentInstallment?.paymentNote
					? paymentInstallment.paymentNote
					: prevState
			);
		}, [ getPaymentTypeForModal, paymentInstallment ] );
		const blurValidationEnabledRef = useRef( false );
		const handleOnClose = useCallback(
			( event: HandleOnCloseProps[0], reason: HandleOnCloseProps[1] ) => {
				blurValidationEnabledRef.current = false;
				setPaymentType( undefined );
				setPaymentDate( undefined );
				setPaymentNote( '' );
				onClose?.( event, reason );
			},
			[ onClose ]
		);

		const currencyLabel = rpcShared.invoices.getCurrencyLabel(
			invoice?.organization?.country
		);

		const isNewOrOfflinePayment =
			paymentInstallment?.status === InstallmentStatus.Unpaid ||
			paymentInstallment?.processor === PaymentProcessor.Offline;
		const [ updatePaidInstallment, { loading: updatePaymentLoading } ] =
			useInvoiceViewPaymentInstallmentPaymentModal_UpdatePaidInstallmentMutation();
		const [ recordOfflinePayment, { loading: offlinePaymentLoading } ] =
			useInvoiceViewPaymentInstallmentPaymentModal_RecordOfflinePaymentMutation();
		const [ unrecordOfflinePaymentOrRefund, { loading: unrecordOfflinePaymentOrRefundLoading }, ] =
			useInvoiceViewPaymentInstallmentPaymentModal_UnrecordOfflinePaymentOrRefundMutation();
		const createOrUpdate = useCallback(
			async ( id: string ) =>
				!paymentInstallment?.paymentDate
					? await recordOfflinePayment( {
						variables: {
							data: {
								// these silly defaults are just to make typescript happy. Validation should prevent using them
								paymentType: paymentType || PaymentType.Cash,
								paymentDate: paymentDate || new Date(),
								paymentNote,
							},
							where: {
								id,
							},
						},
					  } )
					: await updatePaidInstallment( {
						variables: {
							data: {
								paymentNote,
								...( paymentInstallment?.processor === PaymentProcessor.Offline
									? {
										paymentType,
										paymentDate,
										  }
									: {} ),
							},
							where: {
								id,
							},
						},
					  } ),
			[
				paymentType,
				paymentDate,
				paymentInstallment?.paymentDate,
				paymentInstallment?.processor,
				paymentNote,
				recordOfflinePayment,
				updatePaidInstallment,
			]
		);
		const validateDate = useCallback(
			( paymentDateOverride?: Date | string ) => {
				const blurValidationEnabled = blurValidationEnabledRef.current;
				const effectivePaymentDate = paymentDate || paymentDateOverride;
				if (
					effectivePaymentDate &&
					dayjs( effectivePaymentDate ).isSameOrBefore( dayjs(), 'day' )
				) {
					if ( blurValidationEnabled ) {
						setValidatePaymentDate( undefined );
					}
					return true;
				} else {
					if ( blurValidationEnabled ) {
						setValidatePaymentDate( 'Invalid payment date' );
					}
					return false;
				}
			},
			[ paymentDate ]
		);
		const validateMethod = useCallback(
			( paymentTypeOverride?: PaymentType ) => {
				const blurValidationEnabled = blurValidationEnabledRef.current;
				const effectiveOfflinePaymentType = paymentType || paymentTypeOverride;
				// if we're updating a stripe payment, and we haven't set an paymentType, then we're valid!
				if (
					!effectiveOfflinePaymentType &&
					paymentInstallment?.processor === PaymentProcessor.Stripe
				) {
					if ( blurValidationEnabled ) {
						setValidatePaymentType( undefined );
					}
					return true;
				}
				if (
					rpcShared.invoices.isOfflinePaymentType( effectiveOfflinePaymentType )
				) {
					if ( blurValidationEnabled ) {
						setValidatePaymentType( undefined );
					}
					return true;
				} else {
					if ( blurValidationEnabled ) {
						setValidatePaymentType(
							'You must select a payment type to record an offline payment'
						);
					}
					return false;
				}
			},
			[ paymentType, paymentInstallment?.processor ]
		);
		const validate = useCallback(
			( overrides?: {
				paymentType?: PaymentType;
				paymentDate?: Date | string;
			} ) => {
				const dateValidation = validateDate( overrides?.paymentDate );
				const methodValidation = validateMethod( overrides?.paymentType );
				return dateValidation && methodValidation;
			},
			[ validateDate, validateMethod ]
		);

		const onUnrecordPayment = useCallback(
			async (
				installment: InvoiceViewPaymentInstallmentPaymentModalFragment,
				e: React.MouseEvent<HTMLButtonElement>
			) => {
				const res = await unrecordOfflinePaymentOrRefund( {
					variables: {
						where: {
							id: installment.id,
						},
						data: {
							type: RecordOfflinePaymentOrRefundType.Payment,
						},
					},
					refetchQueries: [ 'invoiceViewInvoice_getInvoiceWhere', 'invoiceViewInvoice_getInvoiceHistory', ],
				} );
				if ( res.errors ) {
					res.errors.forEach( ( error ) => showError( error ) );
				} else {
					showSuccess( 'Payment unrecorded.' );
					handleOnClose( e, 'fromCode' );
				}
			},
			[
				unrecordOfflinePaymentOrRefund,
				showError,
				showSuccess,
				handleOnClose
			]
		);

		const { viewingUserIsCustomer, viewingUserIsAPPA } =
			rpcShared.contacts.getViewingUserDocumentRelationship( {
				contact: invoice?.contact,
				viewingUserId: requestingUserBaseId,
			} );
		const viewingUserIsCustomerOrAPPA =
			viewingUserIsCustomer || viewingUserIsAPPA;

		if ( !paymentInstallment ) {
			return null;
		}
		// offline payments can have any date in the past, but stripe must have a date of "now"
		const upperBoundDate = new Date();
		const paymentInstallmentStringValues = invoice
			? rpcShared.invoices.calculateInstallmentToString(
				invoice,
				paymentInstallment
			  )
			: undefined;
		if ( !paymentInstallmentStringValues || !paymentInstallment.amountDue ) {
			return null;
		}
		const offlinePaymentInputsDisabled =
			( paymentInstallment.status &&
				[ InstallmentStatus.Refunded, InstallmentStatus.FullyPaidPartiallyRefunded, ].includes( paymentInstallment.status ) ) ||
			false;

		return (
			<MuiModal
				muiModalProps={ {
					open: !!paymentInstallment,
					onClose: handleOnClose,
					className: styles.container,
				} }
				title={
					paymentInstallment.status === InstallmentStatus.Unpaid
						? 'Record payment'
						: 'Update payment'
				}
				controls={
					<Box className={ styles.buttonContainer }>
						{ paymentInstallment.status === InstallmentStatus.FullyPaid &&
						paymentInstallment.processor === PaymentProcessor.Offline ? (
								<Button
									data-cy='submit-unrecord-payment-button'
									variant='outlined'
									onClick={ async ( e ) => {
										await onUnrecordPayment( paymentInstallment, e );
									} }
									loading={ unrecordOfflinePaymentOrRefundLoading }
								>
								Unrecord
								</Button>
							) : null }
						<Button
							data-cy='submit-createOrUpdate-payment-button'
							onClick={ async ( e ) => {
								blurValidationEnabledRef.current = true;
								if ( validate() ) {
									const response = await createOrUpdate( paymentInstallment.id );
									if ( response.errors ) {
										response.errors.forEach( ( error ) => showError( error ) );
									} else {
										handleOnClose( e, 'fromCode' );
										showSuccess();
									}
								}
							} }
							loading={
								updatePaymentLoading ||
								offlinePaymentLoading ||
								unrecordOfflinePaymentOrRefundLoading
							}
						>
							Save
						</Button>
					</Box>
				}
			>
				<Box className={ styles.columns }>
					<ModalValue
						label='Name'
						value={ paymentInstallmentStringValues.name }
						isBold
					/>
					<ModalValue
						label='Payment method'
						value={
							<InvoiceViewPaymentMethod
								paymentInstallment={ paymentInstallment }
							/>
						}
						isVisible={
							paymentInstallment.status !== InstallmentStatus.Unpaid &&
							paymentInstallment.processor !== PaymentProcessor.Offline
						}
						usePlaceholderBoxIfInvisible
					/>
					<ModalValue
						label={
							paymentInstallment.status === InstallmentStatus.Unpaid
								? `Amount due${ currencyLabel }`
								: `Amount paid${ currencyLabel }`
						}
						value={
							paymentInstallment.status === InstallmentStatus.Unpaid
								? paymentInstallmentStringValues.amountDue
								: paymentInstallmentStringValues.amountPaid
						}
						isBold
					/>
					<ModalValue
						label='Status'
						value={
							<InvoiceViewPaymentInstallmentStatus
								installment={ paymentInstallment }
								isCustomerOrAPPA={ viewingUserIsCustomerOrAPPA }
							/>
						}
					/>
					<ModalValue
						className={ styles.bottom }
						label='Date paid'
						value={ paymentInstallmentStringValues.paymentDate }
						isVisible={ paymentInstallment.processor === PaymentProcessor.Stripe }
					/>
				</Box>
				<Box className={ styles.rows }>
					{ isNewOrOfflinePayment ? (
						<>
							<PaymentInstallmentOfflinePaymentTypeSelect
								value={ paymentType || undefined }
								onChange={ ( value ) => {
									if ( typeof value === 'string' || !value ) {
										setPaymentType( value );
										// gotta pass in the value here because there's a useEffect delay between setting and reading is seems
										validate( { paymentType: value } );
									}
								} }
								data-cy='payment-type'
								errorText={ validatePaymentType }
								disabled={ offlinePaymentInputsDisabled }
							/>
							<DatePicker
								label='Date paid'
								value={ paymentDate }
								onChange={ ( value ) => {
									setPaymentDate( value );
									// gotta pass in the value here because there's a useEffect delay between setting and reading is seems
									validate( { paymentDate: value || undefined } );
								} }
								slotProps={ {
									textField: {
										slotProps: { htmlInput: { 'data-cy': 'payment-date' } },
										variant: 'standard',
									},
								} }
								maxDate={ upperBoundDate ? dayjs( upperBoundDate ) : undefined }
								clearable={ false }
								errorText={ validatePaymentDate }
								disabled={ offlinePaymentInputsDisabled }
							/>
						</>
					) : null }
					<MuiTextArea
						fullWidth
						className={ styles.notes }
						label='payment note (optional)'
						value={ paymentNote }
						onChange={ ( e ) => setPaymentNote( e.target.value ) }
						slotProps={ { htmlInput: { 'data-cy': 'payment-note' } } }
						maxLength={ 500 }
						countCharacters
					/>
				</Box>
			</MuiModal>
		);
	};

InvoiceViewPaymentInstallmentPaymentModal.fragments = {
	invoiceViewPaymentInstallmentPaymentModalFragment,
	invoiceViewPaymentInstallmentPaymentModalInvoiceFragment,
};
